import type { ReactNode } from 'react';

import { isAuthenticated, useAuth } from '@suntex.fim/auth/react';
import { EntityLevel } from '@suntex.fim/enums';
import { SchoolProvider } from '@suntex.fim/school';
import { TeamProvider } from '@suntex.fim/team';

import type { CommonLayoutProps } from './CommonLayout';
import { EntityLayoutShell } from './EntityLayoutShell';

export type EntityLevelLayoutProps = Pick<CommonLayoutProps, 'appVersion'>;

/** Wraps the domain provider for logins that have one; each reads its id from the login. */
function wrapLoginProvider(level: EntityLevel, layout: ReactNode): ReactNode {
  switch (level) {
    case EntityLevel.Team:
      return <TeamProvider>{layout}</TeamProvider>;
    case EntityLevel.School:
      return <SchoolProvider>{layout}</SchoolProvider>;
    default:
      return layout;
  }
}

/**
 * Layout wrapper for routes not scoped by a URL entity (e.g. `/preview`): the
 * entity, and so the sidebar menu, comes from the current login. Team and
 * school logins also get their domain provider, which reads its id from the
 * login.
 */
export function EntityLevelLayout(props: Readonly<EntityLevelLayoutProps>) {
  const { login } = useAuth(isAuthenticated);
  return (
    <EntityLayoutShell
      {...props}
      entityLevel={login.entityLevel}
      entityId={login.entityId}
      wrap={(layout) => wrapLoginProvider(login.entityLevel, layout)}
    />
  );
}
